import React, { useState } from 'react'
import styled from 'styled-components'
import PageTitle from '../../_Components/Common/PageTitle/PageTitle'

const SceneImageReview = props => {
  const { imageList, status, setStatus, updateStatus } = props

  const [preview, setPreview] = useState(null)
  const [riskOnly, setRiskOnly] = useState(false)

  const statusList = [
    { name: 'Reviewing', value: 'P' },
    { name: 'Approved', value: 'Y' },
    { name: 'Disapproved', value: 'N' },
    { name: 'All', value: 'all' },
  ]

  const pct = s => `${(Number(s) * 100).toFixed(1)}%`

  // §6.1 에러 코드 → 어드민 표시 문구
  const errorReasonText = code => {
    switch (code) {
      case 'INSUFFICIENT_CREDIT':
        return '크레딧/한도 소진'
      case 'AUTH_ERROR':
        return '인증 오류 (API 키 확인 필요)'
      case 'INVALID_IMAGE':
        return '이미지 처리 불가'
      case 'RATE_LIMITED':
        return '요청 과다 (재시도 중)'
      case 'TIMEOUT':
        return '응답 지연 (재시도 중)'
      default:
        return '알 수 없는 오류'
    }
  }

  // 딥페이크 의심(>=0.5): 카드 최상단 강조 + 테두리 빨간색
  const isDeepfakeAlert = p =>
    p.hiveDeepfakeScore != null && Number(p.hiveDeepfakeScore) >= 0.5

  // §5.1 위험 신호: AI 생성 90%+ 또는 웹 매칭 EXACT 또는 딥페이크 의심
  const isRisky = p =>
    (p.hiveAiScore != null && Number(p.hiveAiScore) >= 0.9) ||
    p.googleMatchLevel === 'EXACT_MATCH' ||
    isDeepfakeAlert(p)

  // AI 생성 점수 신호 (색상/문구)
  const renderAiScore = p => {
    if (p.hiveErrorReason) {
      return (
        <div className="sig">⚪ AI 생성 검사 실패 — {errorReasonText(p.hiveErrorReason)}</div>
      )
    }
    if (p.hiveAiScore == null) return null
    const score = Number(p.hiveAiScore)
    const dot = score >= 0.9 ? '🔴' : score >= 0.5 ? '🟡' : '🟢'
    return (
      <div className="sig">
        {dot} AI 생성: {pct(score)}
        {p.hiveSourceModel ? ` (${p.hiveSourceModel})` : ''}
      </div>
    )
  }

  // 웹 매칭 신호 (색상/문구)
  const renderWebMatch = p => {
    if (p.googleErrorReason) {
      return (
        <div className="sig">⚪ 웹 매칭 검사 실패 — {errorReasonText(p.googleErrorReason)}</div>
      )
    }
    if (!p.googleMatchLevel) return null
    if (p.googleMatchLevel === 'EXACT_MATCH') {
      return <div className="sig">🔴 웹 매칭: 동일 이미지 {p.googleMatchCount || 0}건</div>
    }
    if (p.googleMatchLevel === 'SIMILAR_MATCH') {
      return <div className="sig">🟡 웹 매칭: 유사 이미지 {p.googleMatchCount || 0}건</div>
    }
    return <div className="sig">🟢 웹 매칭: 없음</div>
  }

  const renderAiSignals = p => {
    // 아직 검사 전이고 에러/결과가 전혀 없으면 pending 표시
    const noResult =
      p.hiveAiScore == null &&
      !p.hiveErrorReason &&
      !p.googleMatchLevel &&
      !p.googleErrorReason
    if (p.aiCheckStatus === 'pending' && noResult) {
      return <div className="sig">⏳ 검사 중...</div>
    }
    return (
      <>
        {isDeepfakeAlert(p) && (
          <div className="sig deepfake">🚨 딥페이크 의심: {pct(p.hiveDeepfakeScore)}</div>
        )}
        {renderAiScore(p)}
        {renderWebMatch(p)}
      </>
    )
  }

  const getDate = d => {
    if (!d) return ''
    const date = new Date(d)
    const kstDate = new Date(
      date.toLocaleString('en-US', { timeZone: 'Asia/Seoul' })
    )

    return (
      `${kstDate.getFullYear()}-` +
      `${String(kstDate.getMonth() + 1).padStart(2, '0')}-` +
      `${String(kstDate.getDate()).padStart(2, '0')} ` +
      `${String(kstDate.getHours()).padStart(2, '0')}:` +
      `${String(kstDate.getMinutes()).padStart(2, '0')}:` +
      `${String(kstDate.getSeconds()).padStart(2, '0')}`
    )
  }

  return (
    <>
      <PageTitle
        mainTitle={'Scene Upload Image Review'}
        v1Link={''}
        navNames={['Scene Image']}
      />
      <SpaceResponsive height={10} />
      <section className="content">
        <div className="row">
          <section className="col-lg-12 connectedSortable">
            <div className="box box-info" style={{ padding: '0 10px' }}>
              <div className="box-body">
                <div style={{ marginTop: 10 }}>
                  <ButtonWrapper style={{ width: 'auto' }}>
                    {statusList.map((s, i) => (
                      <button
                        key={i}
                        style={{
                          backgroundColor:
                            status === s.value ? '#007bff' : '#6c757d',
                        }}
                        onClick={() => setStatus(s.value)}
                      >
                        {s.name}
                      </button>
                    ))}
                    <button
                      style={{
                        backgroundColor: riskOnly ? '#dc3545' : '#6c757d',
                      }}
                      onClick={() => setRiskOnly(v => !v)}
                    >
                      🔴 위험 신호만 보기
                    </button>
                  </ButtonWrapper>
                  <SpaceResponsive height={10} />
                  {(() => {
                    const list = riskOnly ? imageList.filter(isRisky) : imageList
                    if (list.length === 0) {
                      return <EmptyBox>{riskOnly ? '위험 신호가 있는 이미지가 없습니다.' : '해당 상태의 업로드 이미지가 없습니다.'}</EmptyBox>
                    }
                    return (
                  <ImageWrapper>
                    {list.map((p, i) => (
                      <ImageBox key={i} $alert={isDeepfakeAlert(p)}>
                        <MediaArea onClick={() => setPreview(p.image || p.webp)}>
                          <img src={p.webp || p.image} alt="" />
                        </MediaArea>
                        <InfoBox>
                          <div id="meta">
                            <div style={{ fontSize: '11px', color: '#888' }}>
                              seq: {p.seq}
                            </div>
                            {p.userName && (
                              <div>
                                user: {p.userName} ({p.userId})
                              </div>
                            )}
                            {p.regDate && <div>{getDate(p.regDate)}</div>}
                          </div>
                          <AiSignalBox>
                            {renderAiSignals(p)}
                            <div className="tooltip">
                              <div>AI 생성 확률: {p.hiveAiScore != null ? pct(p.hiveAiScore) : '-'}</div>
                              <div>딥페이크 확률: {p.hiveDeepfakeScore != null ? pct(p.hiveDeepfakeScore) : '-'}</div>
                              <div>감지된 생성 모델: {p.hiveSourceModel || '-'}</div>
                              <div>웹 매칭: {p.googleMatchLevel === 'EXACT_MATCH' ? `동일 ${p.googleMatchCount || 0}건` : p.googleMatchLevel === 'SIMILAR_MATCH' ? `유사 ${p.googleMatchCount || 0}건` : p.googleMatchLevel === 'NO_MATCH' ? '없음' : '-'}</div>
                              {p.googleTopEntity && <div>연관 항목: {p.googleTopEntity}</div>}
                              <div>검사 시각: {p.aiCheckedAt ? getDate(p.aiCheckedAt) : '-'}</div>
                            </div>
                          </AiSignalBox>
                        </InfoBox>
                        {p.status !== 'P' && (
                          <img
                            id="status"
                            src={`https://img.stipop.io/image/admin/${
                              p.status === 'Y' ? 'check' : 'cancel'
                            }-icon.png`}
                            alt=""
                          />
                        )}
                        <SpaceResponsive height={10} />
                        {p.status === 'P' && (
                          <div id="button-wrapper">
                            <button
                              style={{ background: '#28a745' }}
                              onClick={() => updateStatus(p.seq, 'Y')}
                            >
                              Approve
                            </button>
                            <button
                              style={{ background: '#dc3545' }}
                              onClick={() => {
                                if (window.confirm('거절하시겠습니까?')) {
                                  updateStatus(p.seq, 'N')
                                }
                              }}
                            >
                              Reject
                            </button>
                          </div>
                        )}
                      </ImageBox>
                    ))}
                  </ImageWrapper>
                    )
                  })()}
                  <SpaceResponsive height={20} />
                </div>
              </div>
            </div>
          </section>
        </div>
      </section>

      {preview && (
        <LightboxOverlay onClick={() => setPreview(null)}>
          <LightboxClose onClick={() => setPreview(null)}>×</LightboxClose>
          <LightboxImg src={preview} alt="" onClick={e => e.stopPropagation()} />
        </LightboxOverlay>
      )}
    </>
  )
}

export default SceneImageReview

const LightboxOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  cursor: zoom-out;
`

const LightboxImg = styled.img`
  max-width: 90vw;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
  cursor: default;
`

const LightboxClose = styled.button`
  position: absolute;
  top: 20px;
  right: 24px;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`

const SpaceResponsive = styled.div`
  height: ${props => props.height}px;
  flex-shrink: 0;
`

const EmptyBox = styled.div`
  width: 100%;
  text-align: center;
  padding: 40px 0;
  color: #888;
`

const ImageWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-gap: 10px;
`

const ImageBox = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  ${props =>
    props.$alert
      ? `
    border: 2px solid #dc3545;
    border-radius: 18px;
    padding: 6px;
    box-shadow: 0 0 0 3px rgba(220,53,69,0.15);
  `
      : ''}

  #status {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 40px;
    height: 40px;
    z-index: 2;
  }

  #button-wrapper {
    width: 100%;
    max-width: 240px;
    display: flex;
    column-gap: 8px;
    justify-content: center;

    button {
      width: 100%;
      border: none;
      outline: none;
      border-radius: 4px;
      color: #fff;
      padding: 6px 0;

      &:hover {
        opacity: 0.85;
      }
    }
  }
`

const ButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin: 10px 0;

  button {
    height: 35px;
    padding: 0 10px;
    box-sizing: border-box;
    margin-left: 10px;
    color: #fff;
    border-radius: 5px;
    border: none;

    &:hover {
      opacity: 0.8;
    }
  }
`

const MediaArea = styled.div`
  width: 100%;
  max-width: 240px;
  aspect-ratio: 1 / 1;
  margin: 0 auto;
  border-radius: 16px;
  overflow: hidden;
  background-color: #000;
  position: relative;
  cursor: zoom-in;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`

const InfoBox = styled.div`
  width: 100%;
  max-width: 240px;
  margin: 0 auto;
  padding: 8px 4px;
  box-sizing: border-box;

  #meta {
    display: flex;
    flex-direction: column;
    row-gap: 2px;
    font-size: 12px;
    color: #333;
    word-break: break-all;
  }
`

const AiSignalBox = styled.div`
  position: relative;
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid #eee;
  display: flex;
  flex-direction: column;
  row-gap: 2px;
  font-size: 12px;
  color: #333;
  cursor: default;

  .sig {
    line-height: 1.4;
  }

  .sig.deepfake {
    font-weight: 700;
    color: #dc3545;
  }

  .tooltip {
    visibility: hidden;
    opacity: 0;
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    z-index: 10;
    width: max-content;
    max-width: 240px;
    padding: 8px 10px;
    background-color: rgba(0, 0, 0, 0.88);
    color: #fff;
    font-size: 11px;
    line-height: 1.5;
    border-radius: 6px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    transition: opacity 0.15s ease;
    pointer-events: none;
  }

  &:hover .tooltip {
    visibility: visible;
    opacity: 1;
  }
`
